// src/pages/AboutPage.jsx - HAKKIMIZDA SAYFASI
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronRight, Heart, Shield, Truck, Users, Award,
  Target, Zap, Globe, TrendingUp, CheckCircle, Star,
  Mail, Phone, MapPin, MessageSquare, Package, Clock
} from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { useSearch } from '../context/SearchContext';
import { openCartSidebar } from '../components/CartSidebar';
import Header from '../components/Header';
import Footer from '../components/Footer';

export default function AboutPage() {
  const navigate = useNavigate();
  const { cartCount } = useCart();
  const { wishlistCount } = useWishlist();
  const { searchQuery, setSearchQuery } = useSearch();

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const stats = [
    { icon: <Users size={32} />, value: '13+ Yıl', label: 'Sektör Tecrübesi', color: '#E5332A' },
    { icon: <Package size={32} />, value: 'Geniş', label: 'Ürün Yelpazesi', color: '#2196F3' },
    { icon: <Truck size={32} />, value: 'Türkiye', label: 'Geneli Teslimat', color: '#06d6a0' },
    { icon: <Star size={32} />, value: '%100', label: 'Müşteri Memnuniyeti', color: '#FFC107' }
  ];

  const values = [
    {
      icon: <Heart size={28} />,
      title: 'Müşteri Odaklı',
      description: 'Müşterilerimizin memnuniyeti her zaman önceliğimizdir. Kaliteli ürünler ve kusursuz hizmet sunmak için çalışıyoruz.',
      color: '#E5332A'
    },
    {
      icon: <Shield size={28} />,
      title: 'Güvenilir',
      description: 'Güvenli ödeme sistemleri ve veri koruma politikalarımız ile alışverişinizin güvende olduğundan emin olabilirsiniz.',
      color: '#2196F3'
    },
    {
      icon: <Zap size={28} />,
      title: 'Hızlı Teslimat',
      description: 'Siparişlerinizi en kısa sürede kapınıza ulaştırıyoruz. Hızlı ve güvenli kargo hizmeti ile yanınızdayız.',
      color: '#FFC107'
    },
    {
      icon: <CheckCircle size={28} />,
      title: 'Kalite Garantisi',
      description: 'Tüm ürünlerimiz kalite kontrolünden geçer ve orijinallik garantisi ile sunulur.',
      color: '#06d6a0'
    }
  ];

  const timeline = [
    { year: '2013', title: 'Kuruluş', description: 'Antalya Manavgat\'ta gıda pazarlama alanında faaliyetlerimize başladık.' },
    { year: '2014', title: 'Ürün Çeşitlendirme', description: 'Alkolsüz şampanya ve özel gün ürünleri portföyümüze eklendi.' },
    { year: '2017', title: 'Büyüme', description: 'Zeytinyağı ve kahve çeşitleri ile ürün yelpazemizi genişlettik.' },
    { year: '2020', title: 'E-Ticaret', description: 'Online satış kanalları ve dijital platformlarda yerini aldık.' },
    { year: '2026', title: 'Günümüz', description: 'Türkiye geneline ulaşan güvenilir bir marka olduk.' }
  ];

  return (
    <div className="about-page">
      <Style />

      <Header
        wishlistCount={wishlistCount}
        cartCount={cartCount}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        onSearch={handleSearch}
        onCartClick={openCartSidebar}
        onWishlistClick={() => navigate('/wishlist')}
        onCampaignsClick={() => navigate('/campaigns')}
      />

      {/* Hero Section */}
      <section className="about-hero">
        <div className="hero-background"></div>
        <div className="container">
          <div className="breadcrumb">
            <span onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>Anasayfa</span>
            <ChevronRight size={16} />
            <span>Hakkımızda</span>
          </div>

          <div className="hero-content">
            <div className="hero-text">
              <h1>Trona Gıda Pazarlama Hakkında</h1>
              <p>
                Antalya Manavgat merkezli olarak faaliyet gösteren firmamız, gıda sektöründe kaliteli 
                ürünler sunmaktadır. Alkolsüz şampanya, zeytinyağı, kahve çeşitleri ve daha birçok 
                üründe hem toptan hem perakende satış hizmeti vermekteyiz.
              </p>
              <div className="hero-buttons">
                <button className="primary-btn" onClick={() => navigate('/')}>
                  <Star size={20} />
                  Alışverişe Başla
                </button>
                <button className="secondary-btn" onClick={() => navigate('/contact')}>
                  <MessageSquare size={18} />
                  İletişime Geç
                </button>
              </div>
            </div>

            <div className="hero-image">
              <div className="floating-card card-1">
                <div className="card-icon" style={{ background: '#E5332A15', color: '#E5332A' }}>
                  <Heart size={24} />
                </div>
                <div>
                  <strong>13+ Yıl</strong>
                  <span>Sektör Deneyimi</span>
                </div>
              </div>
              <div className="floating-card card-2">
                <div className="card-icon" style={{ background: '#FFC10715', color: '#FFC107' }}>
                  <Award size={24} />
                </div>
                <div>
                  <strong>Kalite</strong>
                  <span>Garantisi</span>
                </div>
              </div>
              <div className="floating-card card-3">
                <div className="card-icon" style={{ background: '#06d6a015', color: '#06d6a0' }}>
                  <Package size={24} />
                </div>
                <div>
                  <strong>Geniş</strong>
                  <span>Ürün Yelpazesi</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <div key={index} className="stat-card">
                <div className="stat-icon" style={{ background: `${stat.color}15`, color: stat.color }}>
                  {stat.icon}
                </div>
                <div className="stat-info">
                  <div className="stat-value">{stat.value}</div>
                  <div className="stat-label">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="mission-section">
        <div className="container">
          <div className="mission-grid">
            <div className="mission-card">
              <div className="mission-icon">
                <Target size={40} />
              </div>
              <h3>Misyonumuz</h3>
              <p>
                Müşterilerimize, gıda sektöründe kaliteli ve güvenilir ürünler sunmak. Alkolsüz şampanya, 
                sızma zeytinyağı, kahve çeşitleri ve gıda ürünlerinde sektörün öncü markalarından biri 
                olmak. Hem toptan hem perakende satış ile geniş bir müşteri kitlesine ulaşmak.
              </p>
            </div>

            <div className="mission-card">
              <div className="mission-icon vision">
                <Globe size={40} />
              </div>
              <h3>Vizyonumuz</h3>
              <p>
                Gıda sektöründe kalite ve güvenin adresi olmak. Müşteri memnuniyetini ön planda tutarak, 
                Türkiye'nin her yerinde tercih edilen, güvenilir bir marka olmak. Yenilikçi ürün 
                portföyümüzle sektörde fark yaratmak ve sürdürülebilir büyüme sağlamak.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="values-section">
        <div className="container">
          <div className="section-header">
            <h2>Değerlerimiz</h2>
            <p>Bizi biz yapan değerler</p>
          </div>

          <div className="values-grid">
            {values.map((value, index) => (
              <div key={index} className="value-card">
                <div className="value-icon" style={{ background: `${value.color}15`, color: value.color }}>
                  {value.icon}
                </div>
                <h4>{value.title}</h4>
                <p>{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="timeline-section">
        <div className="container">
          <div className="section-header">
            <h2>Yolculuğumuz</h2>
            <p>Geçmişten bugüne hikayemiz</p>
          </div>

          <div className="timeline">
            {timeline.map((item, index) => (
              <div key={index} className="timeline-item">
                <div className="timeline-dot"></div>
                <div className="timeline-content">
                  <div className="timeline-year">{item.year}</div>
                  <h4>{item.title}</h4>
                  <p>{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Trona Gıda Pazarlama Ailesine Katılın</h2>
            <p>Kaliteli gıda ürünleri ve güvenilir hizmet anlayışımızla sizlere de hizmet vermekten mutluluk duyarız.</p>
            <div className="cta-buttons">
              <button className="cta-btn primary" onClick={() => navigate('/')}>
                <Star size={20} />
                Alışverişe Başla
              </button>
              <button className="cta-btn secondary" onClick={() => navigate('/contact')}>
                <Mail size={20} />
                Bize Ulaşın
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function Style() {
  return <style>{`
    * { margin: 0; padding: 0; box-sizing: border-box; -webkit-tap-highlight-color: transparent; }
    
    body { 
      font-family: 'Inter', -apple-system, sans-serif; 
      background: #f8f9fa; 
    }
    
    .about-page { 
      min-height: 100vh; 
      background: #f8f9fa; 
    }
    
    .container { 
      max-width: 1320px; 
      margin: 0 auto; 
      padding: 0 20px; 
    }

    /* ==================== BREADCRUMB ==================== */
    .breadcrumb {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 20px 0;
      font-size: 14px;
      color: rgba(255, 255, 255, 0.8);
    }

    .breadcrumb span {
      transition: color 0.2s;
    }

    .breadcrumb span:hover {
      color: #fff;
    }

    .breadcrumb span:last-child {
      color: #fff;
      font-weight: 600;
    }

    /* ==================== HERO SECTION ==================== */
    .about-hero {
      background: linear-gradient(135deg, #2B2B2B 0%, #1a1a1a 100%);
      padding: 0 0 80px;
      position: relative;
      overflow: hidden;
    }

    .hero-background {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: 
        radial-gradient(circle at 20% 50%, rgba(229, 51, 42, 0.15), transparent 50%),
        radial-gradient(circle at 80% 80%, rgba(6, 214, 160, 0.1), transparent 50%);
      pointer-events: none;
    }

    .hero-content {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 60px;
      align-items: center;
      padding: 40px 0;
      position: relative;
      z-index: 1;
    }

    .hero-text h1 {
      font-size: 56px;
      font-weight: 800;
      color: #fff;
      margin-bottom: 24px;
      line-height: 1.1;
      letter-spacing: -1px;
    }

    .hero-text p {
      font-size: 18px;
      line-height: 1.7;
      color: rgba(255, 255, 255, 0.8);
      margin-bottom: 32px;
    }

    .hero-buttons {
      display: flex;
      gap: 16px;
    }

    .primary-btn {
      background: #E5332A;
      color: #fff;
      border: none;
      padding: 16px 32px;
      border-radius: 12px;
      font-weight: 700;
      font-size: 16px;
      cursor: pointer;
      transition: all 0.3s;
      box-shadow: 0 4px 12px rgba(229, 51, 42, 0.3);
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .primary-btn:hover {
      background: #c72a22;
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(229, 51, 42, 0.4);
    }

    .secondary-btn {
      background: rgba(255, 255, 255, 0.1);
      backdrop-filter: blur(10px);
      color: #fff;
      border: 2px solid rgba(255, 255, 255, 0.2);
      padding: 14px 28px;
      border-radius: 12px;
      font-weight: 700;
      font-size: 16px;
      cursor: pointer;
      transition: all 0.3s;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .secondary-btn:hover {
      background: rgba(255, 255, 255, 0.2);
      border-color: rgba(255, 255, 255, 0.4);
    }

    /* Hero Image with Floating Cards */
    .hero-image {
      position: relative;
      height: 400px;
    }

    .floating-card {
      position: absolute;
      background: #fff;
      border-radius: 16px;
      padding: 20px;
      display: flex;
      align-items: center;
      gap: 16px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
      animation: float 3s ease-in-out infinite;
      border: 1px solid #e9ecef;
    }

    .card-icon {
      width: 56px;
      height: 56px;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .floating-card strong {
      display: block;
      font-size: 24px;
      font-weight: 800;
      color: #2B2B2B;
      line-height: 1;
      margin-bottom: 4px;
    }

    .floating-card span {
      font-size: 13px;
      color: #868e96;
      font-weight: 600;
    }

    .card-1 {
      top: 10%;
      left: 0;
      animation-delay: 0s;
    }

    .card-2 {
      top: 40%;
      right: 10%;
      animation-delay: 1s;
    }

    .card-3 {
      bottom: 10%;
      left: 20%;
      animation-delay: 2s;
    }

    @keyframes float {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(-20px); }
    }

    /* ==================== STATS SECTION ==================== */
    .stats-section {
      margin-top: -40px;
      padding: 0 0 80px;
      position: relative;
      z-index: 2;
    }

    .stats-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 24px;
    }

    .stat-card {
      background: #fff;
      border-radius: 20px;
      padding: 32px;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 16px;
      box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
      transition: all 0.3s;
      border: 2px solid transparent;
    }

    .stat-card:hover {
      transform: translateY(-8px);
      box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
      border-color: #E5332A;
    }

    .stat-icon {
      width: 72px;
      height: 72px;
      border-radius: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .stat-info {
      text-align: center;
    }

    .stat-value {
      font-size: 32px;
      font-weight: 800;
      color: #2B2B2B;
      margin-bottom: 4px;
    }

    .stat-label {
      font-size: 14px;
      color: #868e96;
      font-weight: 600;
    }

    /* ==================== MISSION SECTION ==================== */
    .mission-section {
      padding: 80px 0;
    }

    .mission-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 32px;
    }

    .mission-card {
      background: #fff;
      border-radius: 24px;
      padding: 48px;
      box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
      transition: all 0.3s;
      border: 2px solid transparent;
    }

    .mission-card:hover {
      transform: translateY(-4px);
      box-shadow: 0 12px 40px rgba(0, 0, 0, 0.1);
      border-color: #E5332A;
    }

    .mission-icon {
      width: 80px;
      height: 80px;
      background: linear-gradient(135deg, #E5332A, #ff4436);
      border-radius: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      margin-bottom: 24px;
    }

    .mission-icon.vision {
      background: linear-gradient(135deg, #2196F3, #1976D2);
    }

    .mission-card h3 {
      font-size: 28px;
      font-weight: 800;
      color: #2B2B2B;
      margin-bottom: 16px;
    }

    .mission-card p {
      font-size: 16px;
      line-height: 1.7;
      color: #495057;
    }

    /* ==================== VALUES SECTION ==================== */
    .values-section {
      padding: 80px 0;
      background: #fff;
    }

    .section-header {
      text-align: center;
      margin-bottom: 64px;
    }

    .section-header h2 {
      font-size: 42px;
      font-weight: 800;
      color: #2B2B2B;
      margin-bottom: 12px;
    }

    .section-header p {
      font-size: 18px;
      color: #868e96;
    }

    .values-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 32px;
    }

    .value-card {
      text-align: center;
      padding: 32px;
      border-radius: 20px;
      transition: all 0.3s;
      border: 2px solid transparent;
    }

    .value-card:hover {
      background: #f8f9fa;
      transform: translateY(-4px);
      border-color: #e9ecef;
    }

    .value-icon {
      width: 72px;
      height: 72px;
      border-radius: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 24px;
    }

    .value-card h4 {
      font-size: 20px;
      font-weight: 700;
      color: #2B2B2B;
      margin-bottom: 12px;
    }

    .value-card p {
      font-size: 15px;
      line-height: 1.6;
      color: #495057;
    }

    /* ==================== TIMELINE SECTION ==================== */
    .timeline-section {
      padding: 80px 0;
    }

    .timeline {
      max-width: 900px;
      margin: 0 auto;
      position: relative;
      padding-left: 40px;
    }

    .timeline::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 4px;
      background: linear-gradient(180deg, #E5332A, #06d6a0);
      border-radius: 2px;
    }

    .timeline-item {
      position: relative;
      padding-bottom: 48px;
    }

    .timeline-item:last-child {
      padding-bottom: 0;
    }

    .timeline-dot {
      position: absolute;
      left: -48px;
      top: 8px;
      width: 16px;
      height: 16px;
      background: #E5332A;
      border-radius: 50%;
      border: 4px solid #fff;
      box-shadow: 0 0 0 4px #E5332A20;
    }

    .timeline-content {
      background: #fff;
      border-radius: 16px;
      padding: 24px;
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
      transition: all 0.3s;
      border: 2px solid transparent;
    }

    .timeline-content:hover {
      transform: translateX(8px);
      box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
      border-color: #E5332A;
    }

    .timeline-year {
      display: inline-block;
      background: #E5332A;
      color: #fff;
      padding: 6px 16px;
      border-radius: 8px;
      font-weight: 700;
      font-size: 14px;
      margin-bottom: 12px;
    }

    .timeline-content h4 {
      font-size: 20px;
      font-weight: 700;
      color: #2B2B2B;
      margin-bottom: 8px;
    }

    .timeline-content p {
      font-size: 15px;
      color: #495057;
      line-height: 1.6;
    }

    /* ==================== CTA SECTION ==================== */
    .cta-section {
      padding: 80px 0;
      background: linear-gradient(135deg, #E5332A, #ff4436);
      position: relative;
      overflow: hidden;
    }

    .cta-section::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: 
        radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.1), transparent 50%),
        radial-gradient(circle at 80% 80%, rgba(0, 0, 0, 0.1), transparent 50%);
      pointer-events: none;
    }

    .cta-content {
      text-align: center;
      position: relative;
      z-index: 1;
    }

    .cta-content h2 {
      font-size: 42px;
      font-weight: 800;
      color: #fff;
      margin-bottom: 16px;
    }

    .cta-content p {
      font-size: 18px;
      color: rgba(255, 255, 255, 0.9);
      margin-bottom: 32px;
      max-width: 700px;
      margin-left: auto;
      margin-right: auto;
    }

    .cta-buttons {
      display: flex;
      gap: 16px;
      justify-content: center;
    }

    .cta-btn {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 16px 32px;
      border-radius: 12px;
      font-weight: 700;
      font-size: 16px;
      cursor: pointer;
      transition: all 0.3s;
      border: none;
    }

    .cta-btn.primary {
      background: #fff;
      color: #E5332A;
    }

    .cta-btn.primary:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(255, 255, 255, 0.3);
    }

    .cta-btn.secondary {
      background: rgba(255, 255, 255, 0.15);
      backdrop-filter: blur(10px);
      color: #fff;
      border: 2px solid rgba(255, 255, 255, 0.3);
    }

    .cta-btn.secondary:hover {
      background: rgba(255, 255, 255, 0.25);
      border-color: rgba(255, 255, 255, 0.5);
    }

    /* ==================== RESPONSIVE ==================== */
    @media (max-width: 992px) {
      .hero-content {
        grid-template-columns: 1fr;
        gap: 40px;
      }

      .hero-text h1 {
        font-size: 42px;
      }

      .hero-image {
        height: 300px;
      }

      .stats-grid {
        grid-template-columns: repeat(2, 1fr);
      }

      .mission-grid {
        grid-template-columns: 1fr;
      }

      .values-grid {
        grid-template-columns: repeat(2, 1fr);
      }
        
    }

    @media (max-width: 768px) {
      .container {
        padding: 0 16px;
      }

      .about-hero {
        padding: 0 0 60px;
      }

      .hero-text h1 {
        font-size: 32px;
      }

      .hero-text p {
        font-size: 16px;
      }

      .hero-buttons {
        flex-direction: column;
      }

      .primary-btn,
      .secondary-btn {
        width: 100%;
        justify-content: center;
      }

      .hero-image {
        display: none;
      }

      .stats-grid {
        grid-template-columns: 1fr;
        gap: 16px;
      }

      .stat-card {
        padding: 24px;
      }

      .section-header h2 {
        font-size: 32px;
      }

      .section-header p {
        font-size: 16px;
      }

      .values-grid {
        grid-template-columns: 1fr;
      }

      .mission-card {
        padding: 32px;
      }

      .mission-card h3 {
        font-size: 24px;
      }

      .timeline {
        padding-left: 32px;
      }

      .timeline-dot {
        left: -40px;
      }

      .cta-content h2 {
        font-size: 32px;
      }

      .cta-content p {
        font-size: 16px;
      }

      .cta-buttons {
        flex-direction: column;
        padding: 0 16px;
      }

      .cta-btn {
        width: 100%;
        justify-content: center;
      }
    }

    @media (max-width: 480px) {
      .container {
        padding: 0 12px;
      }

      .hero-text h1 {
        font-size: 28px;
      }

      .hero-text p {
        font-size: 15px;
      }

      .stat-icon {
        width: 60px;
        height: 60px;
      }

      .stat-value {
        font-size: 28px;
      }

      .stat-label {
        font-size: 13px;
      }

      .mission-icon {
        width: 64px;
        height: 64px;
      }

      .mission-card {
        padding: 24px;
      }

      .mission-card h3 {
        font-size: 22px;
      }

      .mission-card p {
        font-size: 15px;
      }

      .section-header h2 {
        font-size: 28px;
      }

      .value-icon {
        width: 60px;
        height: 60px;
      }

      .value-card h4 {
        font-size: 18px;
      }

      .value-card p {
        font-size: 14px;
      }

      .cta-content h2 {
        font-size: 28px;
      }

      .cta-content p {
        font-size: 15px;
      }

      .cta-btn {
        padding: 14px 24px;
        font-size: 15px;
      }
    }

    @media (max-width: 360px) {
      .hero-text h1 {
        font-size: 24px;
      }

      .section-header h2 {
        font-size: 24px;
      }

      .stat-card {
        padding: 20px;
      }

      .mission-card {
        padding: 20px;
      }

      .value-card {
        padding: 24px;
      }
    }
  `}</style>;
}